import useLocalStorage from './useLocalStorage'
import axios from 'axios'

const { stores, selectedStore, selectedStoreFilter, user, permissionsByStores } = useLocalStorage()
const storesWithReviewPlatforms = ref()

export default function useStore() {
  const getStoreById = (storeId: number) => {
    return stores.value.find((store) => store.id === Number(storeId))
  }

  const updateStores = async () => {
    const { data } = await axios.get('/stores')
    stores.value = data.data
  }

  const selectStore = (storeId: number) => {
    selectedStore.value = storeId
  }

  const objectiveSlug = computed(() => {
    let objectiveSlug = 'no-objective'
    if (stores.value.length && stores.value[0] && stores.value[0].objective?.slug) {
      objectiveSlug = stores.value[0].objective?.slug
    }
    return objectiveSlug
  })

  const objectiveLabel = computed(() => {
    let objectiveLabel
    if (stores.value.length && stores.value[0] && stores.value[0].objective?.label) {
      objectiveLabel = stores.value[0].objective?.label
    }
    return objectiveLabel
  })

  const getStoresByPermission = (permission: string) => {
    const newStoresArray = []
    stores.value.forEach((store) => {
      if (
        permissionsByStores.value &&
        permissionsByStores.value[store.id] &&
        permissionsByStores.value[store.id][permission]
      ) {
        newStoresArray.push(store)
      }
    })
    return newStoresArray
  }

  const getAllowedUserStoreIds = () => {
    const allowedStores = stores.value.filter((store) => {
      return permissionsByStores.value[store.id] && Object.keys(permissionsByStores.value[store.id]).length > 0
    })
    return allowedStores.map((store) => store.id)
  }

  const getStoresCompanyProducts = async () => {
    const { data } = await axios.get(`merchants/${user.value.merchant.id}/company-products`)
    return data
  }

  const getStoreSchedulingPlatformUrl = (storeId?: string | number) => {
    const hasSelectedStoreId = !!storeId && Number(storeId) !== 0
    const newSelectedStore = hasSelectedStoreId ? getStoreById(Number(storeId)) : stores.value[0]

    return newSelectedStore?.schedulingPlatformUrl?.csm
  }

  const getStoresByModule = async (module: string, includePongoSuite = true) => {
    try {
      const products = await getStoresCompanyProducts()

      let pongoSuiteStores = []
      if (products['pongo-suite'] && includePongoSuite) {
        pongoSuiteStores = Object.values(products['pongo-suite'])?.flatMap((moduleData: any) => {
          return Object.values(moduleData.stores)?.map((store: any) => ({
            id: store.id,
            name: store.name
          }))
        })
      }

      let moduleStores = []
      if (products[module]) {
        moduleStores = Object.values(products[module])?.flatMap((moduleData: any) => {
          return Object.values(moduleData.stores)?.map((store: any) => ({
            id: store.id,
            name: store.name
          }))
        })
      }

      const storeMap = new Map()
      pongoSuiteStores?.forEach((store: any) => {
        storeMap.set(store.id, store)
      })
      moduleStores?.forEach((store: any) => {
        storeMap.set(store.id, store)
      })

      return Array.from(storeMap.values())
    } catch {
      return []
    }
  }

  const getMultiselectStoresByModule = async (module: string) => {
    const stores = await getStoresByModule(module)
    return stores.map((store: any) => ({
      id: store.id,
      name: store.name,
      disabled: !getAllowedUserStoreIds().includes(store.id)
    }))
  }

  const storesWithoutReviewLink = computed(() => {
    return stores.value.filter((store) => !store.googlePlaceId).map((store) => store.id)
  })

  // Also called outside components (e.g. analytics in the router afterEach), where onMounted would only warn
  if (getCurrentInstance()) {
    onMounted(() => {
      if ((!selectedStore.value || selectedStore.value < 0) && selectedStore.value !== 0) {
        selectedStore.value = 0
      }
    })
  }

  return {
    getAllowedUserStoreIds,
    getMultiselectStoresByModule,
    getStoreById,
    getStoresByModule,
    getStoresByPermission,
    getStoreSchedulingPlatformUrl,
    getStoresCompanyProducts,
    objectiveLabel,
    objectiveSlug,
    selectedStore,
    selectedStoreFilter,
    selectStore,
    storesWithoutReviewLink,
    storesWithReviewPlatforms,
    updateStores
  }
}
